import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { profileApi, metaApi, subscriptionApi } from '../services/api';
import api from '../services/api';
import { Link } from 'react-router-dom';
import Modal, { useModal } from '../components/Modal';
import LoadingOverlay from '../components/LoadingOverlay';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { loadStripe } from '@stripe/stripe-js';
import { stripeErrorMessage } from '../utils/stripeErrors';

export default function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const { modal, show, close } = useModal();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    business_name: user?.business_name || '',
    business_address: user?.business_address || '',
  });
  const [loading, setLoading] = useState(false);
  const [savedCard, setSavedCard]     = useState<any>(null);
  const [cardLoading, setCardLoading] = useState(true);
  const [editCard, setEditCard]       = useState(false);
  const [holderName, setHolderName]   = useState('');
  const [cardReady, setCardReady]     = useState(false);
  const [savingCard, setSavingCard]   = useState(false);
  const [stripeKeyError, setStripeKeyError] = useState<string | null>(null);
  const cardMountRef = React.useRef<HTMLDivElement>(null);
  const stripeRef    = React.useRef<any>(null);
  const cardRef      = React.useRef<any>(null);
  const [subAction, setSubAction] = useState(false);
  // Molti Like può promuovere i contenuti della Pagina collegata? (null = non verificato)
  const [adsAccess, setAdsAccess] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user?.has_facebook) { setAdsAccess(null); return; }
    metaApi.status().then(r => setAdsAccess(r.data?.ads_access ?? null)).catch(() => setAdsAccess(null));
  }, [user?.has_facebook, user?.facebook_page_name]);

  useEffect(() => {
    subscriptionApi.paymentMethod()
      .then(r => setSavedCard(r.data.card))
      .catch(() => {})
      .finally(() => setCardLoading(false));
  }, []);

  useEffect(() => {
    if (!editCard) return;
    let mounted = true;
    setStripeKeyError(null);
    setCardReady(false);
    api.get('/stripe-key')
      .then(res => {
        if (!mounted) return;
        const pk = res.data?.publishable_key;
        if (!pk) {
          setStripeKeyError('Chiave Stripe non disponibile. Riprova più tardi.');
          return;
        }
        return loadStripe(pk);
      })
      .then(stripe => {
        if (!stripe || !mounted || !cardMountRef.current) return;
        stripeRef.current = stripe;
        const elements = stripe.elements();
        const card = elements.create('card', {
          style: {
            base: { color: '#E2E8F5', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '15px', '::placeholder': { color: '#4E5A72' }, iconColor: '#4F72FF' },
            invalid: { color: '#F87171' },
          },
          hidePostalCode: true,
        });
        card.mount(cardMountRef.current);
        card.on('ready', () => mounted && setCardReady(true));
        cardRef.current = card;
      })
      .catch(() => {
        if (mounted) setStripeKeyError('Impossibile caricare il modulo di pagamento. Riprova più tardi.');
      });
    return () => { mounted = false; cardRef.current?.destroy(); cardRef.current = null; };
  }, [editCard]);

  const saveCard = async () => {
    if (!stripeRef.current || !cardRef.current) return;
    if (!holderName.trim()) {
      show({ title: 'Dati mancanti', message: "Inserisci il nome dell'intestatario.", type: 'warning', onConfirm: close });
      return;
    }
    setSavingCard(true);
    try {
      const { paymentMethod, error } = await stripeRef.current.createPaymentMethod({
        type: 'card', card: cardRef.current, billing_details: { name: holderName.trim() },
      });
      if (error) {
        show({ title: 'Carta non valida', message: stripeErrorMessage(error), type: 'error', onConfirm: close });
        setSavingCard(false);
        return;
      }
      await subscriptionApi.updatePaymentMethod({ payment_method: paymentMethod.id });
      setSavedCard({ brand: paymentMethod.card?.brand, last4: paymentMethod.card?.last4, exp_month: paymentMethod.card?.exp_month, exp_year: paymentMethod.card?.exp_year });
      setEditCard(false);
      setHolderName('');
      show({ title: 'Carta salvata', message: 'Il metodo di pagamento è stato aggiornato.', type: 'success', onConfirm: close });
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Impossibile salvare la carta.';
      show({ title: 'Errore', message: msg, type: 'error', onConfirm: close });
    } finally {
      setSavingCard(false);
    }
  };

  const removeCard = () => {
    show({
      title: 'Rimuovi carta',
      message: "Se rimuovi la carta, al prossimo rinnovo l'abbonamento verrà sospeso automaticamente. Vuoi continuare?",
      type: 'warning',
      confirmText: 'Rimuovi',
      cancelText: 'Annulla',
      onConfirm: async () => {
        close();
        try {
          await subscriptionApi.removePaymentMethod();
          setSavedCard(null);
        } catch {
          show({ title: 'Errore', message: 'Impossibile rimuovere la carta. Riprova.', type: 'error', onConfirm: close });
        }
      },
      onCancel: close,
    });
  };

  const upd = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setLoading(true);
    try {
      await profileApi.update(form);
      await refreshUser();
      setEditing(false);
    } catch {
      show({ title: 'Errore salvataggio', message: 'Impossibile salvare le modifiche. Riprova.', type: 'error', onConfirm: close });
    } finally {
      setLoading(false);
    }
  };

  const disconnectMeta = () => {
    show({
      title: 'Scollega account',
      message: 'Vuoi scollegare Facebook e Instagram? Potrai ricollegarli in seguito.',
      type: 'confirm',
      confirmText: 'Scollega',
      cancelText: 'Annulla',
      onConfirm: async () => {
        close();
        await metaApi.disconnect();
        await refreshUser();
      },
      onCancel: close,
    });
  };

  const initials = user?.name?.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() || '?';
  const planLabel = user?.subscription_plan
    ? user.subscription_plan.charAt(0).toUpperCase() + user.subscription_plan.slice(1)
    : null;

  const cancelSubscription = () => {
    show({
      title: 'Cancella abbonamento',
      message: "L'abbonamento rimarrà attivo fino alla fine del periodo già pagato. Alla scadenza la promozione dei tuoi post si interromperà automaticamente.",
      type: 'confirm',
      confirmText: 'Cancella abbonamento',
      cancelText: 'Annulla',
      onConfirm: async () => {
        close();
        setSubAction(true);
        try {
          await subscriptionApi.cancel();
          await refreshUser();
        } catch {
          show({ title: 'Errore', message: 'Impossibile cancellare. Riprova.', type: 'error', onConfirm: close });
        } finally {
          setSubAction(false);
        }
      },
      onCancel: close,
    });
  };

  const resumeSubscription = async () => {
    setSubAction(true);
    try {
      await subscriptionApi.resume();
      await refreshUser();
    } catch {
      show({ title: 'Errore', message: 'Impossibile riattivare. Riprova.', type: 'error', onConfirm: close });
    } finally {
      setSubAction(false);
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="page">
      {savingCard && <LoadingOverlay title="Salvataggio della carta..." subtitle="Un momento, stiamo verificando la carta con Stripe" icon="credit_card" />}
      {loading && <LoadingOverlay title="Salvataggio del profilo..." subtitle="Un momento" icon="person" />}
      {cardLoading && <LoadingOverlay title="Caricamento del profilo..." subtitle="Un momento" icon="person" />}
      {editCard && !cardReady && !stripeKeyError && <LoadingOverlay title="Caricamento del modulo carta..." subtitle="Un momento" icon="credit_card" />}
      {subAction && <LoadingOverlay title="Aggiornamento dell'abbonamento..." subtitle="Un momento" icon="sync" />}
      {modal && (
        <Modal
          isOpen
          title={modal.title}
          message={modal.message}
          type={modal.type}
          confirmText={modal.confirmText}
          cancelText={modal.cancelText}
          onConfirm={modal.onConfirm}
          onCancel={modal.onCancel ?? close}
        />
      )}

      <h1>Profilo</h1>

      {/* Avatar card */}
      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div className="avatar">{initials}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-1)' }}>{user?.name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{user?.email}</div>
          {planLabel && (
            <div className="plan-badge">
              <span className="mi mi-16">bolt</span> {planLabel}
            </div>
          )}
        </div>
      </div>

      {/* Dati account */}
      <div>
        <span className="section-label">Dati account</span>
        <div className="card" style={{ padding: '14px 16px' }}>
          {editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Nome completo</label>
                <input className="form-input" value={form.name} onChange={e => upd('name', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Telefono</label>
                <input className="form-input" type="tel" value={form.phone} onChange={e => upd('phone', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Nome attività</label>
                <input className="form-input" value={form.business_name} onChange={e => upd('business_name', e.target.value)} />
              </div>
              <div className="form-group">
                <AddressAutocomplete
                  label="Indirizzo attività"
                  value={form.business_address}
                  onChange={v => upd('business_address', v)}
                  placeholder="Via Roma 1, 09100 Cagliari"
                  onSelect={parsed => {
                    const full = [parsed.address, parsed.zip, parsed.city].filter(Boolean).join(' ');
                    if (full) upd('business_address', full);
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                <button className="btn btn-primary" onClick={save} disabled={loading}>
                  <span className="mi mi-20">check</span>
                  Salva modifiche
                </button>
                <button className="btn btn-cancel" onClick={() => setEditing(false)}>Annulla</button>
              </div>
            </div>
          ) : (
            <>
              <div className="data-rows">
                <div className="data-row"><span className="data-key">Nome</span><span className="data-val">{user?.name}</span></div>
                <div className="data-row"><span className="data-key">Email</span><span className="data-val">{user?.email}</span></div>
                <div className="data-row"><span className="data-key">Telefono</span><span className="data-val">{user?.phone || '—'}</span></div>
                <div className="data-row"><span className="data-key">Attività</span><span className="data-val">{user?.business_name}</span></div>
                <div className="data-row"><span className="data-key">Indirizzo</span><span className="data-val" style={{ fontSize: 13 }}>{user?.business_address}</span></div>
              </div>
              <button className="btn btn-outline" onClick={() => setEditing(true)} style={{ marginTop: 14, fontSize: 13, padding: 11 }}>
                <span className="mi mi-18">edit</span> Modifica
              </button>
            </>
          )}
        </div>
      </div>

      {/* Abbonamento */}
      <div>
        <span className="section-label">Abbonamento</span>
        <div className="card" style={{ padding: '14px 16px' }}>
          <div className="data-rows">
            <div className="data-row">
              <span className="data-key">Piano</span>
              <span className="data-val" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                {planLabel || 'Nessuno'}
              </span>
            </div>
            <div className="data-row" style={{ borderBottom: (user?.subscription_status === 'canceling' || (user?.subscription_status === 'active' && user?.subscription_plan === 'trial' && user?.subscription_ends_at) || (user?.subscription_status === 'active' && user?.subscription_plan !== 'trial' && user?.subscription_renews_at)) ? undefined : 'none' }}>
              <span className="data-key">Stato</span>
              <span className="data-val" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  width: 7, height: 7, borderRadius: '50%', display: 'inline-block',
                  background: user?.subscription_status === 'active' ? 'var(--success)'
                    : (user?.subscription_status === 'canceling' || user?.subscription_status === 'paused') ? 'var(--warning)'
                    : 'var(--text-3)'
                }} />
                {user?.subscription_status === 'active' ? 'Attivo'
                  : user?.subscription_status === 'paused' ? 'In pausa'
                  : user?.subscription_status === 'canceling' ? 'In scadenza'
                  : 'Inattivo'}
              </span>
            </div>
            {user?.subscription_status === 'canceling' && user?.subscription_ends_at && (
              <div className="data-row" style={{ borderBottom: 'none' }}>
                <span className="data-key">Scade il</span>
                <span className="data-val" style={{ fontSize: 13, color: 'var(--warning)' }}>
                  {formatDate(user.subscription_ends_at)}
                </span>
              </div>
            )}
            {user?.subscription_status === 'active' && user?.subscription_plan === 'trial' && user?.subscription_ends_at && (
              <div className="data-row" style={{ borderBottom: 'none' }}>
                <span className="data-key">Scade il</span>
                <span className="data-val" style={{ fontSize: 13, color: 'var(--warning)' }}>
                  {formatDate(user.subscription_ends_at)}
                </span>
              </div>
            )}
            {user?.subscription_status === 'active' && user?.subscription_plan !== 'trial' && user?.subscription_renews_at && (
              <div className="data-row" style={{ borderBottom: 'none' }}>
                <span className="data-key">Rinnovo il</span>
                <span className="data-val" style={{ fontSize: 13 }}>
                  {formatDate(user.subscription_renews_at)}
                </span>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
            <Link to="/plans" className="btn btn-outline" style={{ fontSize: 13, padding: 11, display: 'flex' }}>
              Gestisci piano
            </Link>

            {user?.subscription_status === 'active' && user?.subscription_plan !== 'trial' && (
              <button
                className="btn btn-cancel"
                style={{ fontSize: 13, padding: 11 }}
                onClick={cancelSubscription}
                disabled={subAction}
              >
                Cancella abbonamento
              </button>
            )}

            {user?.subscription_status === 'canceling' && (
              <button
                className="btn btn-primary"
                style={{ fontSize: 13, padding: 11 }}
                onClick={resumeSubscription}
                disabled={subAction}
              >
                <span className="mi mi-18">undo</span> Riattiva abbonamento
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Social collegati */}
      <div>
        <span className="section-label">Social collegati</span>
        <div className="card" style={{ padding: '14px 16px' }}>
          {(user?.has_facebook || user?.has_instagram) ? (
            <>
              <div className="data-rows">
                {user.has_facebook && (
                  <div className="data-row">
                    <span className="data-key" style={{ display: 'flex', alignItems: 'center', gap: 5, paddingTop: 0 }}>
                      <span style={{ fontWeight: 900, color: '#1877F2', fontSize: 15 }}>f</span>Facebook
                    </span>
                    <span className="data-val">{user.facebook_page_name}</span>
                  </div>
                )}
                {user.has_instagram && (
                  <div className="data-row">
                    <span className="data-key" style={{ display: 'flex', alignItems: 'center', gap: 5, paddingTop: 0 }}>
                      <span className="mi mi-16" style={{ color: '#E1306C' }}>photo_camera</span>Instagram
                    </span>
                    <span className="data-val">@{user.instagram_username}</span>
                  </div>
                )}
                {user.has_facebook && adsAccess !== null && (
                  <div className="data-row" style={{ borderBottom: 'none' }}>
                    <span className="data-key" style={{ display: 'flex', alignItems: 'center', gap: 5, paddingTop: 0 }}>
                      <span className="mi mi-16" style={{ color: adsAccess ? 'var(--success)' : 'var(--warning)' }}>{adsAccess ? 'verified' : 'hourglass_empty'}</span>Promozione
                    </span>
                    <span className="data-val" style={{ color: adsAccess ? 'var(--success)' : 'var(--warning)' }}>{adsAccess ? 'Abilitata' : 'Non ancora abilitata'}</span>
                  </div>
                )}
              </div>
              <button className="btn btn-secondary" onClick={disconnectMeta}
                style={{ marginTop: 14, fontSize: 13, padding: 11, color: 'var(--error)', borderColor: 'rgba(248,113,113,0.2)' }}>
                Scollega account
              </button>
            </>
          ) : (
            <Link to="/connect" className="btn btn-primary" style={{ display: 'flex' }}>
              <span className="mi mi-18">link</span> Collega i tuoi social
            </Link>
          )}
        </div>
      </div>

      {/* Metodo di pagamento */}
      <div>
        <span className="section-label">Metodo di pagamento</span>
        <div className="card" style={{ padding: '14px 16px' }}>
          {cardLoading ? null : !editCard ? (
            <>
              {savedCard ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <span className="mi" style={{ fontSize: 28, color: 'var(--primary)' }}>credit_card</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{savedCard.brand?.toUpperCase()} •••• {savedCard.last4}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Scade {String(savedCard.exp_month).padStart(2, '0')}/{savedCard.exp_year}</div>
                  </div>
                  <span className="badge badge-success" style={{ padding: '3px 8px', fontSize: 11 }}>
                    <span className="mi mi-14">check_circle</span>&nbsp;Attiva
                  </span>
                </div>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 14 }}>Nessuna carta salvata.</p>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline" style={{ fontSize: 13, padding: 11 }} onClick={() => setEditCard(true)}>
                  <span className="mi mi-18">credit_card</span>{savedCard ? 'Cambia carta' : 'Aggiungi carta'}
                </button>
                {savedCard && (
                  <button className="btn btn-danger" style={{ fontSize: 13, padding: 11, width: 'auto', flex: 'none', paddingLeft: 16, paddingRight: 16 }} onClick={removeCard}>
                    <span className="mi mi-18">delete</span>Rimuovi
                  </button>
                )}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {stripeKeyError ? (
                <div className="alert error">
                  <span className="mi mi-18">error_outline</span>
                  <span>{stripeKeyError}</span>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Intestatario carta</label>
                    <input className="form-input" value={holderName} onChange={e => setHolderName(e.target.value)} placeholder="Mario Rossi" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Dati carta</label>
                    <div ref={cardMountRef} style={{ background: 'var(--s2)', border: '1px solid var(--border-md)', borderRadius: 'var(--radius-sm)', padding: '14px 16px', minHeight: 48 }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--s2)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <span className="mi mi-16" style={{ color: 'var(--success)' }}>lock</span>
                    <span style={{ fontSize: 11, color: 'var(--text-2)' }}>Pagamento sicuro via <strong style={{ color: 'var(--text-1)' }}>Stripe</strong></span>
                  </div>
                </>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={{ fontSize: 13, padding: 11 }} onClick={saveCard} disabled={savingCard || !cardReady || !!stripeKeyError}>
                  Salva carta
                </button>
                <button className="btn btn-cancel" style={{ fontSize: 13, padding: 11, flex: 'none', width: 'auto', paddingLeft: 16, paddingRight: 16 }} onClick={() => setEditCard(false)}>Annulla</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dati fatturazione */}
      <div>
        <span className="section-label">Dati di fatturazione</span>
        <div className="card" style={{ padding: '14px 16px' }}>
          <div className="data-rows">
            <div className="data-row">
              <span className="data-key">Intestazione</span>
              <span className="data-val" style={{ fontSize: 13 }}>
                {user?.billing_company || (user?.billing_first_name ? `${user.billing_first_name} ${user.billing_last_name}` : '—')}
              </span>
            </div>
            <div className="data-row" style={{ borderBottom: 'none' }}>
              <span className="data-key">{user?.billing_type === 'azienda' ? 'P.IVA' : 'C.F.'}</span>
              <span className="data-val" style={{ fontSize: 12, letterSpacing: '0.03em' }}>
                {user?.vat_number || user?.tax_code || '—'}
              </span>
            </div>
          </div>
          <Link to="/billing" className="btn btn-outline" style={{ marginTop: 14, fontSize: 13, padding: 11, display: 'flex' }}>
            <span className="mi mi-18">receipt_long</span> Gestisci dati fatturazione
          </Link>
        </div>
      </div>

      {/* Logout */}
      <button className="btn btn-danger" onClick={logout}>
        <span className="mi mi-20">logout</span> Esci dall'account
      </button>
    </div>
  );
}
